import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router';
import routes from '@commons/routes';

interface User {
  username: string;
  fullInfo: string;
}

interface UserContextType {
  user: User | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  cleanLogout: () => void;
  logoutWithCallback: (callback?: () => void) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(() => {
    const userJson = localStorage.getItem('user');
    return userJson ? JSON.parse(userJson) : null;
  });

  // Listen for storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = () => {
      const userJson = localStorage.getItem('user');
      const newUser = userJson ? JSON.parse(userJson) : null;

      // If user was logged in but now isn't, update state
      if (user && !newUser) {
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user]);

  const login = (token: string, user: User) => {
    localStorage.setItem('userToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
  };

  const cleanLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const logout = () => {
    cleanLogout();
    navigate(routes.root);
  };

  const logoutWithCallback = (callback?: () => void) => {
    cleanLogout();
    if (callback) {
      callback();
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        login,
        logout,
        cleanLogout,
        logoutWithCallback,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};
